import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { MarketingStatus } from '@/integrations/algolia/types';
import { commonMessages } from '@/messages/common';

dayjs.extend(customParseFormat);

const parseLaunchDate = (launchDateString: string): dayjs.Dayjs | null => {
  if (launchDateString) {
    const date = dayjs(launchDateString, [
      'DD.MM.YYYY',
      'DD-MM-YYYY',
      'DD/MM/YYYY',
      'DD MM YYYY',
      'DD MMM YYYY',
      'DD MMMM YYYY',
      'YYYY-MM-DD',
    ]);
    if (date.isValid()) {
      return date;
    }
  }
  return null;
};

export const isNewerThanSixMonths = (
  launchDate: number | dayjs.Dayjs | Date | string | null,
): boolean => {
  if (dayjs.isDayjs(launchDate) || launchDate instanceof Date) {
    const sixMonthsAgo = dayjs().subtract(6, 'month');
    const now = Date.now();
    return (
      dayjs(launchDate).isAfter(sixMonthsAgo) && dayjs(launchDate).isBefore(now)
    );
  }
  if (typeof launchDate === 'number') {
    const now = Date.now();
    if (launchDate * 1000 > now) {
      return true;
    }
    const sixMonthsAgoInMilliseconds = dayjs().subtract(6, 'month').valueOf();
    const launchDateMS = launchDate * 1000;

    // if COM_LAUNCH_DATE is null , the data from algolia will give a biggest value, because algolia not support null value;
    return launchDateMS > sixMonthsAgoInMilliseconds && launchDateMS < now;
  }
  if (typeof launchDate === 'string') {
    const launchDateParsed = parseLaunchDate(launchDate);
    return isNewerThanSixMonths(launchDateParsed);
  }
  return false;
};

/**
 * Get the translated product marketing status label by default label names
 * @param statuses Default label names
 * @param launchDate
 * @param isPDP
 * @example
 * getLabel(['Boutique & Online Exclusive'], null, true);
 * // returns "Exclusivité Boutique & En ligne" current language is FR
 */
export const getLabel = (
  statuses: string[] | null,
  launchDate: number | Date | string | null,
  isPDP = false,
) => {
  const isNew = isNewerThanSixMonths(launchDate);
  if (isNew && !isPDP) {
    return commonMessages.new;
  }

  const priorityOrder = Object.values(MarketingStatus);
  for (const status of priorityOrder) {
    if (statuses?.includes(status)) {
      switch (status) {
        case MarketingStatus.BOUTIQUE_AND_ONLINE_EXCLUSIVE:
          return commonMessages.boutiqueAndOnlineExclusive;
        case MarketingStatus.BOUTIQUE_EXCLUSIVE:
          return commonMessages.boutiqueExclusive;
        case MarketingStatus.ONLINE_EXCLUSIVE:
          return commonMessages.onlineExclusive;
        case MarketingStatus.COUNTRY_EXCLUSIVE:
          return commonMessages.countryExclusive;
        case MarketingStatus.LIMITED:
          return commonMessages.limitedEdition;
        case MarketingStatus.CAPSULE:
          return commonMessages.capsule;
      }
    }
  }

  if (isNew) {
    return commonMessages.new;
  }

  return null;
};

export const getPageTitle = (text?: string) => {
  if (!text) return null;

  switch (text) {
    case 'Boutique & Online Exclusive':
      return commonMessages.boutiqueAndOnlineExclusive;
    case 'Boutique Exclusive':
      return commonMessages.boutiqueExclusive;
    case 'Online Exclusive':
      return commonMessages.onlineExclusive;
    case 'Country Exclusive':
      return commonMessages.countryExclusive;
    case 'Limited':
      return commonMessages.limitedEdition;
    case 'Capsule':
      return commonMessages.capsule;
    case 'Search':
      return commonMessages.search;
    default:
      return null;
  }
};
